<template>
  <v-container fluid class="ncrm-email-marketing">
    <NcrmHomePageHeader
      icon="mdi-email-newsletter"
      :title="$t('ncrm.emailMarketing.title')"
      :subtitle="$t('ncrm.emailMarketing.subtitle')"
      data-tour="email-marketing-header"
    >
      <v-btn color="secondary" variant="tonal" :loading="syncing" data-tour="email-marketing-sync" @click="runSync">
        <v-icon start>mdi-sync</v-icon>
        {{ $t("ncrm.emailMarketing.sync.button") }}
      </v-btn>
    </NcrmHomePageHeader>

    <v-alert v-if="errorMessage" type="error" class="mt-4">
      {{ errorMessage }}
    </v-alert>

    <v-alert
      v-if="showStaleStatsBanner"
      type="info"
      class="mt-4"
      icon="mdi-database-sync-outline"
    >
      <div class="d-flex flex-wrap align-center ga-3">
        <span>{{ $t("ncrm.emailMarketing.sync.bannerText") }}</span>
        <v-spacer />
        <v-btn size="small" color="primary" :loading="syncing" @click="runSync">
          <v-icon start size="16">mdi-sync</v-icon>
          {{ $t("ncrm.emailMarketing.sync.button") }}
        </v-btn>
        <v-btn size="small" variant="text" @click="openMailjetIntegration">
          {{ $t("ncrm.emailMarketing.sync.openMailjet") }}
          <v-icon end size="14">mdi-open-in-new</v-icon>
        </v-btn>
      </div>
    </v-alert>

    <div class="d-flex flex-wrap align-center ga-3 mt-4" data-tour="email-marketing-filters">
      <v-select
        v-model="filters.accountId"
        :items="accountItems"
        :label="$t('ncrm.emailMarketing.filters.account')"
        hide-details
        clearable
        class="ncrm-email-marketing__account-select"
      />
      <NcrmSegmentedControl
        v-model="filters.period"
        :items="periodItems"
        :aria-label="$t('ncrm.emailMarketing.filters.periodAriaLabel')"
      />
    </div>

    <v-card class="mt-4">
      <v-tabs v-model="activeTab" color="primary" data-tour="email-marketing-tabs">
        <v-tab value="dashboard">{{ $t("ncrm.emailMarketing.tabs.dashboard") }}</v-tab>
        <v-tab value="campaigns">{{ $t("ncrm.emailMarketing.tabs.campaigns") }}</v-tab>
        <v-tab value="planner">{{ $t("ncrm.emailMarketing.tabs.planner") }}</v-tab>
      </v-tabs>
      <v-divider />
      <v-card-text>
        <v-window v-model="activeTab" :touch="false">
          <v-window-item value="dashboard">
            <NcrmEmailMarketingDashboardTab
              :overview="overview"
              :campaigns="campaigns"
              :loading="loading"
            />
          </v-window-item>
          <v-window-item value="campaigns">
            <NcrmEmailMarketingCampaignsTab
              :campaigns="campaigns"
              :overview="overview"
              :loading="loading"
              @notify="showFeedback"
            />
          </v-window-item>
          <v-window-item value="planner">
            <NcrmEmailMarketingPlannerTab :campaigns="campaigns" @notify="showFeedback" />
          </v-window-item>
        </v-window>
      </v-card-text>
    </v-card>

    <v-snackbar v-model="feedback.show" :color="feedback.color" timeout="2500">
      {{ feedback.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import NcrmHomePageHeader from "@/components/ncrm/shared/NcrmHomePageHeader.vue";
import NcrmSegmentedControl from "@/components/ncrm/shared/NcrmSegmentedControl.vue";
import NcrmEmailMarketingDashboardTab from "./NcrmEmailMarketingDashboardTab.vue";
import NcrmEmailMarketingCampaignsTab from "./NcrmEmailMarketingCampaignsTab.vue";
import NcrmEmailMarketingPlannerTab from "./NcrmEmailMarketingPlannerTab.vue";
import {
  EMAIL_MARKETING_PERIODS,
  fetchEmailMarketingOverview,
  fetchEmailMarketingCampaigns,
  triggerEmailMarketingSync,
} from "@/Utile/ncrmEmailMarketing.mjs";

export default {
  name: "NcrmEmailMarketingWorkspace",
  components: {
    NcrmHomePageHeader,
    NcrmSegmentedControl,
    NcrmEmailMarketingDashboardTab,
    NcrmEmailMarketingCampaignsTab,
    NcrmEmailMarketingPlannerTab,
  },
  data() {
    return {
      activeTab: "dashboard",
      loading: false,
      syncing: false,
      errorMessage: "",
      overview: null,
      campaigns: [],
      filters: {
        accountId: null,
        period: "90",
      },
      feedback: {
        show: false,
        message: "",
        color: "success",
      },
    };
  },
  computed: {
    // Banner de ghidare: exista campanii importate, dar statisticile oficiale
    // nu au fost sincronizate (dashboard-ul ruleaza pe estimari sau e gol).
    showStaleStatsBanner() {
      const totals = this.overview?.totals;
      if (!totals) return false;
      return Number(totals.campaignsCount) > 0 && totals.metricsSource !== "counters";
    },
    accountItems() {
      const accounts = this.overview?.accounts || [];
      return accounts.map((account) => ({
        title: account.name,
        value: account.id,
      }));
    },
    periodItems() {
      return EMAIL_MARKETING_PERIODS.map((item) => ({
        value: item.value,
        label: this.$t(item.labelKey),
      }));
    },
  },
  watch: {
    "filters.accountId"() {
      this.loadData();
    },
    "filters.period"() {
      this.loadData();
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      this.errorMessage = "";
      try {
        const query = {
          accountId: this.filters.accountId || undefined,
          period: this.filters.period,
        };
        const [overview, campaignsResult] = await Promise.all([
          fetchEmailMarketingOverview(query),
          fetchEmailMarketingCampaigns(query),
        ]);
        this.overview = overview;
        this.campaigns = campaignsResult.campaigns || [];
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error || this.$t("ncrm.emailMarketing.errors.loadFailed");
      } finally {
        this.loading = false;
      }
    },
    showFeedback({ message, color = "success" } = {}) {
      this.feedback = { show: true, message, color };
    },
    async runSync() {
      this.syncing = true;
      try {
        const result = await triggerEmailMarketingSync({
          accountId: this.filters.accountId || undefined,
        });
        this.showFeedback({
          message: result.ok
            ? this.$t("ncrm.emailMarketing.sync.success")
            : this.$t("ncrm.emailMarketing.sync.partial"),
          color: result.ok ? "success" : "warning",
        });
        await this.loadData();
      } catch (error) {
        this.showFeedback({
          message: error?.response?.data?.error || this.$t("ncrm.emailMarketing.sync.error"),
          color: "error",
        });
      } finally {
        this.syncing = false;
      }
    },
    openMailjetIntegration() {
      this.$router.push({ path: "/ncrm/data-integrations", query: { tab: "mailjet" } });
    },
  },
};
</script>

<style scoped>
.ncrm-email-marketing__account-select {
  max-width: 320px;
  min-width: 220px;
}
</style>
